import React, { useState } from "react";
import { Capacitor } from "@capacitor/core";
import { Browser } from "@capacitor/browser";
import { api, ApiError, type Tenancy } from "../../api";
import { useToast } from "../../ToastContext";
import { useLanguage } from "../../i18n";
import { FURNISHING_ITEM_KEYS } from "../../furnishingItems";
import { fileToResizedDataUrl } from "../../photoUtils";

// See RentalAgreement.tsx for why native needs the Browser plugin instead of
// window.open().
const isNative = Capacitor.isNativePlatform();

function fmtINR(n: number) {
  return "₹" + Number(n).toLocaleString("en-IN");
}

// Blocks the rest of the dashboard until a newly-added tenant reviews and
// confirms their move-in details — see TenantApp.tsx, which renders this
// instead of the normal view whenever an active tenancy has
// onboardingStatus === 'pending'.
export function TenantOnboarding({ tenancy, onDone }: { tenancy: Tenancy; onDone: () => void }) {
  const { t } = useLanguage();
  const toast = useToast();
  const [name, setName] = useState(tenancy.name);
  const [depositStatus, setDepositStatus] = useState(tenancy.depositStatus);
  const [depositPaymentMode, setDepositPaymentMode] = useState(tenancy.depositPaymentMode);
  const [showPayPanel, setShowPayPanel] = useState(false);
  const [payingDeposit, setPayingDeposit] = useState(false);
  const [showCashForm, setShowCashForm] = useState(false);
  const [cashProofPhoto, setCashProofPhoto] = useState("");
  const [cashPhotoBusy, setCashPhotoBusy] = useState(false);
  const [confirmingCash, setConfirmingCash] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [verifiedName, setVerifiedName] = useState(tenancy.digilockerVerifiedName ?? null);
  const [verifying, setVerifying] = useState(false);

  const ownerUpiId = tenancy.property?.ownerUpiId ?? null;
  const ownerName = tenancy.property?.ownerName ?? null;
  const ownerBankHolder = tenancy.property?.ownerBankAccountHolder ?? null;
  const ownerBankNumber = tenancy.property?.ownerBankAccountNumber ?? null;
  const ownerBankIfsc = tenancy.property?.ownerBankIfsc ?? null;
  const hasBank = !!(ownerBankHolder && ownerBankNumber && ownerBankIfsc);
  const depositPayMode: "upi" | "bank" | null = ownerUpiId ? "upi" : hasBank ? "bank" : null;

  function upiIntentLink(vpa: string, payeeName: string, amount: number, note: string) {
    const params = new URLSearchParams({ pa: vpa, pn: payeeName, am: String(amount), cu: "INR", tn: note });
    return `upi://pay?${params.toString()}`;
  }

  async function copyText(text: string) {
    try {
      await navigator.clipboard.writeText(text);
      toast(t("tenantPayments.upiIdCopiedToast"));
    } catch {
      // clipboard access denied — nothing more we can do here
    }
  }

  async function verifyWithDigilocker() {
    setVerifying(true);
    const tab = isNative ? null : window.open("", "_blank");
    try {
      const { url } = await api.post("/api/digilocker/start", { tenancyId: tenancy.id });
      if (!url) {
        tab?.close();
        return;
      }
      if (isNative) await Browser.open({ url });
      else if (tab) tab.location.href = url;
    } catch (err) {
      tab?.close();
      toast(err instanceof ApiError ? err.message : t("common.somethingWrong"));
    } finally {
      setVerifying(false);
    }
  }

  // Deposit pays the owner directly (UPI deep link or bank transfer) —
  // never routed through ToLetBook's own account — so, like rent, the
  // tenant self-attests once they've actually paid.
  async function confirmDepositPaid() {
    setPayingDeposit(true);
    setShowPayPanel(false);
    try {
      const { tenancy: updated } = await api.post(`/api/tenancy/${tenancy.id}/deposit-confirm`, { mode: "online" });
      setDepositStatus(updated.depositStatus);
      setDepositPaymentMode(updated.depositPaymentMode);
      toast(t("tenantOnboarding.depositPaidToast"));
    } catch (err) {
      toast(err instanceof ApiError ? err.message : t("common.somethingWrong"));
    } finally {
      setPayingDeposit(false);
    }
  }

  async function handleCashProofFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !file.type.startsWith("image/")) return;
    setCashPhotoBusy(true);
    try {
      setCashProofPhoto(await fileToResizedDataUrl(file));
    } finally {
      setCashPhotoBusy(false);
    }
  }

  async function confirmCashPayment() {
    setConfirmingCash(true);
    setError("");
    try {
      await api.post(`/api/tenancy/${tenancy.id}/deposit-confirm`, { mode: "cash", proofPhoto: cashProofPhoto || null });
      setDepositStatus("paid");
      setDepositPaymentMode("cash");
      setShowCashForm(false);
      toast(t("tenantOnboarding.depositPaidToast"));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t("common.somethingWrong"));
    } finally {
      setConfirmingCash(false);
    }
  }

  async function submit() {
    if (!name.trim()) return setError(t("tenantOnboarding.errNameRequired"));
    if (tenancy.deposit > 0 && depositStatus !== "paid") return setError(t("tenantOnboarding.errDepositRequired"));
    if (!confirmed) return setError(t("tenantOnboarding.errMustConfirm"));
    setBusy(true);
    setError("");
    try {
      await api.post(`/api/tenancies/${tenancy.id}/complete-onboarding`, { tenantName: name.trim() });
      toast(t("tenantOnboarding.completedToast"));
      onDone();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t("common.somethingWrong"));
    } finally {
      setBusy(false);
    }
  }

  // A multi-house property keeps furnishing per house — use this tenancy's
  // own unit when it belongs to one, falling back to the property-level
  // fields for a plain single-unit property.
  const unit = tenancy.unitId ? tenancy.property?.units?.find((u) => u.id === tenancy.unitId) ?? null : null;
  const facilities = tenancy.property?.facilities ?? [];
  const furnishingStatus = unit?.furnishingStatus ?? tenancy.property?.furnishingStatus ?? null;
  const communityType = unit?.communityType ?? tenancy.property?.communityType ?? null;
  const floorNumber = unit?.floorNumber ?? tenancy.property?.floorNumber ?? null;
  const furnishingItemEntries = Object.entries(unit?.furnishingItems ?? tenancy.property?.furnishingItems ?? {}).filter(
    ([, count]) => Number(count) > 0
  );

  return (
    <div style={{ padding: 16 }}>
      <div className="reg-title">{t("tenantOnboarding.title")}</div>
      <div className="reg-sub">{tenancy.property?.label}</div>
      {error && <div className="error-banner">{error}</div>}

      <div className="field">
        <label>{t("tenantOnboarding.nameLabel")}</label>
        <div className="input-wrap">
          <input value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="field-hint">{t("tenantOnboarding.nameHint")}</div>
      </div>

      <div className="card">
        <div className="card-row">
          <span className="sub">{t("tenantOnboarding.rentLabel")}</span>
          <b>{fmtINR(tenancy.rent)}</b>
        </div>
        <div className="card-row" style={{ marginTop: 8 }}>
          <span className="sub">{t("tenantOnboarding.dueDayLabel")}</span>
          <b>{t("tenantOnboarding.dueDayValue", { day: tenancy.rentDueDay })}</b>
        </div>
      </div>

      <div className="card">
        <div className="card-row">
          <div>
            <div className="sub">{t("tenantOnboarding.depositLabel")}</div>
            <b>{fmtINR(tenancy.deposit)}</b>
          </div>
          {depositStatus === "paid" ? (
            <span className="badge paid">
              {depositPaymentMode === "cash" ? t("tenantOnboarding.depositPaidCashBadge") : t("tenantOnboarding.depositPaidBadge")}
            </span>
          ) : !showCashForm ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 8, alignItems: "flex-end" }}>
              {depositPayMode && (
                <button className="btn-solid btn-sm" disabled={payingDeposit} onClick={() => setShowPayPanel((v) => !v)}>
                  {payingDeposit
                    ? t("tenantOnboarding.payingBtn")
                    : depositPayMode === "upi"
                      ? t("tenantPayments.payViaUpi", { amount: fmtINR(tenancy.deposit) })
                      : t("tenantPayments.payViaBank", { amount: fmtINR(tenancy.deposit) })}
                </button>
              )}
              {!depositPayMode && (
                <button className="btn-solid btn-sm" disabled={payingDeposit} onClick={confirmDepositPaid}>
                  {payingDeposit ? t("tenantOnboarding.payingBtn") : t("tenantPayments.markPaidNoDetailsBtn", { amount: fmtINR(tenancy.deposit) })}
                </button>
              )}
              <button type="button" className="link-btn" onClick={() => setShowCashForm(true)}>
                {t("tenantOnboarding.paidInCashLink")}
              </button>
            </div>
          ) : null}
        </div>

        {depositStatus !== "paid" && !showCashForm && showPayPanel && depositPayMode && (
          <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid var(--line)" }}>
            {depositPayMode === "upi" ? (
              <>
                <div className="sub">{t("tenantPayments.ownerUpiIdLabel")}</div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4 }}>
                  <b style={{ fontSize: 15 }}>{ownerUpiId}</b>
                  <button type="button" className="link-btn" onClick={() => copyText(ownerUpiId!)}>
                    {t("tenantPayments.copyUpiBtn")}
                  </button>
                </div>
                <a
                  href={upiIntentLink(ownerUpiId!, ownerName ?? "ToLetBook Owner", tenancy.deposit, t("tenantOnboarding.depositPaymentDesc"))}
                  className="btn-outline btn-sm"
                  style={{ marginTop: 10, display: "block", textAlign: "center" }}
                >
                  {t("tenantPayments.openUpiAppBtn")}
                </a>
              </>
            ) : (
              <>
                <div className="sub">{t("tenantPayments.ownerBankDetailsLabel")}</div>
                <div style={{ marginTop: 6, fontSize: 14 }}>
                  <div>{ownerBankHolder}</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 2 }}>
                    <b>{ownerBankNumber}</b>
                    <button type="button" className="link-btn" onClick={() => copyText(ownerBankNumber!)}>
                      {t("tenantPayments.copyUpiBtn")}
                    </button>
                  </div>
                  <div style={{ marginTop: 2 }}>{ownerBankIfsc}</div>
                </div>
              </>
            )}
            <button className="btn-solid btn-sm" style={{ marginTop: 10 }} disabled={payingDeposit} onClick={confirmDepositPaid}>
              {t("tenantPayments.markPaidUpiBtn")}
            </button>
          </div>
        )}

        {depositStatus !== "paid" && showCashForm && (
          <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid var(--line)" }}>
            <div className="field-hint" style={{ marginBottom: 8 }}>{t("tenantOnboarding.cashProofHint")}</div>
            <input type="file" accept="image/*" id="cash-proof-input" style={{ display: "none" }} onChange={handleCashProofFile} />
            {cashProofPhoto ? (
              <div className="aadhaar-slots" style={{ maxWidth: 140 }}>
                <div className="aadhaar-slot">
                  <div className="aadhaar-slot-filled">
                    <img src={cashProofPhoto} alt="" onClick={() => document.getElementById("cash-proof-input")?.click()} />
                    <button
                      type="button"
                      className="aadhaar-slot-remove"
                      onClick={() => setCashProofPhoto("")}
                      aria-label={t("assignTenantForm.removeAadhaarPhotoAria")}
                    >
                      &times;
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="aadhaar-slots" style={{ maxWidth: 140 }}>
                <button
                  type="button"
                  className="aadhaar-slot-add"
                  disabled={cashPhotoBusy}
                  onClick={() => document.getElementById("cash-proof-input")?.click()}
                >
                  <span>{cashPhotoBusy ? "…" : "+"}</span>
                  <small>{t("tenantOnboarding.cashProofUploadLabel")}</small>
                </button>
              </div>
            )}
            <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
              <button className="btn-outline btn-sm" style={{ flex: 1 }} onClick={() => setShowCashForm(false)}>
                {t("common.cancel")}
              </button>
              <button className="btn-solid btn-sm" style={{ flex: 1 }} disabled={confirmingCash} onClick={confirmCashPayment}>
                {confirmingCash ? t("common.saving") : t("tenantOnboarding.confirmCashBtn")}
              </button>
            </div>
          </div>
        )}
      </div>

      {tenancy.property?.ownerPlan === "premium" && (
        <div className="card">
          <div className="sub" style={{ marginBottom: 8, fontWeight: 700 }}>{t("tenantOnboarding.identityVerificationLabel")}</div>
          <div className="card-row">
            <span className="sub">{t("tenantOnboarding.yourIdentityLabel")}</span>
            {verifiedName ? (
              <span className="badge paid">{t("rentalAgreement.digilockerVerifiedBadge")}</span>
            ) : (
              <span className="badge vacant">{t("rentalAgreement.digilockerNotVerifiedBadge")}</span>
            )}
          </div>
          {verifiedName ? (
            <div className="sub" style={{ marginTop: 2, fontSize: 13 }}>{verifiedName}</div>
          ) : (
            <>
              <div className="field-hint" style={{ marginTop: 4 }}>{t("tenantOnboarding.identityVerificationHint")}</div>
              <button className="btn-outline btn-sm" style={{ marginTop: 10 }} disabled={verifying} onClick={verifyWithDigilocker}>
                {verifying ? t("rentalAgreement.verifyingDigilockerBtn") : t("rentalAgreement.verifyDigilockerBtn")}
              </button>
            </>
          )}
        </div>
      )}

      {(facilities.length > 0 || furnishingStatus || communityType || furnishingItemEntries.length > 0) && (
        <div className="card">
          <div className="sub" style={{ marginBottom: 8 }}>{t("tenantOnboarding.amenitiesLabel")}</div>
          {(furnishingStatus || communityType) && (
            <div className="chip-row" style={{ marginBottom: facilities.length || furnishingItemEntries.length ? 10 : 0 }}>
              {furnishingStatus && <div className="chip selected">{t(`editPropertyForm.furnishing_${furnishingStatus}`)}</div>}
              {communityType && <div className="chip selected">{t(`editPropertyForm.community_${communityType}`)}</div>}
              {floorNumber && <div className="chip selected">{t("editPropertyForm.floorChipLabel", { floor: floorNumber })}</div>}
            </div>
          )}
          {furnishingItemEntries.length > 0 && (
            <div className="chip-row" style={{ marginBottom: facilities.length ? 10 : 0 }}>
              {furnishingItemEntries.map(([key, count]) => (
                <div className="chip selected" key={key}>
                  {count}× {(FURNISHING_ITEM_KEYS as readonly string[]).includes(key) ? t(`editPropertyForm.item_${key}`) : key}
                </div>
              ))}
            </div>
          )}
          {facilities.length > 0 && (
            <div className="chip-row">
              {facilities.map((f) => (
                <div className="chip selected" key={f}>
                  {f}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <label style={{ display: "flex", gap: 8, alignItems: "flex-start", margin: "18px 0" }}>
        <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} style={{ marginTop: 3 }} />
        <span className="sub">{t("tenantOnboarding.confirmLabel")}</span>
      </label>

      <button className="btn-solid" disabled={busy} onClick={submit}>
        {busy ? t("common.saving") : t("tenantOnboarding.submitBtn")}
      </button>
    </div>
  );
}
